import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const VisualContainer = styled.div`
  position: relative;
  width: 100%;
  height: 480px;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 24px;
  border: 1px solid ${({ theme }) => theme.colors.primary.main}40;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px ${({ theme }) => theme.colors.primary.main}15;
  transition: all 0.5s ease;
  
  @media (max-width: 768px) {
    height: 280px;
    border-radius: 16px;
  }
`;

const Canvas = styled.canvas`
  width: 100%;
  height: 100%;
  display: block;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  font-family: 'JetBrains Mono', monospace, 'Courier New';
  color: ${({ theme }) => theme.colors.primary.main};
  padding: 24px;
`;

const UserCountDisplay = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 48px;
  font-weight: 900;
  letter-spacing: -1px;
  text-shadow: 0 0 20px ${({ theme }) => theme.colors.primary.main}80;
  
  @media (max-width: 768px) {
    font-size: 32px;
    gap: 8px;
  }
`;

const UserIcon = styled.svg`
  width: 56px;
  height: 56px;
  filter: drop-shadow(0 0 20px ${({ theme }) => theme.colors.primary.main}80);
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }
`;

const TechVisual = () => {
    const canvasRef = useRef(null);
    const [readings, setReadings] = useState({ temp: 36.5, count: 2 });
    const peopleRef = useRef([]);
    const doorPos = { x: 50, y: 92 }; // Bottom center door position (0-100 scale)

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        let animationFrameId;

        const cols = 48;
        const rows = 36;

        // Initialize some people
        if (peopleRef.current.length === 0) {
            peopleRef.current = Array.from({ length: 3 }, (_, i) => ({
                id: i,
                x: doorPos.x,
                y: doorPos.y,
                targetX: 20 + Math.random() * 60,
                targetY: 20 + Math.random() * 60,
                intensity: 0,
                state: 'entering',
                speed: 0.15 + Math.random() * 0.1,
                waitTimer: Math.random() * 2000
            }));
        }

        const getThermalColor = (heat) => {
            if (heat > 0.9) return `rgba(255, 60, 40, ${Math.min(1, heat)})`; // Hot Red
            if (heat > 0.7) return `rgba(255, 180, 0, ${heat})`; // Warm Orange-Yellow
            if (heat > 0.4) return `rgba(40, 220, 100, ${heat})`; // Cool Lime Green
            if (heat > 0.15) return `rgba(20, 100, 255, ${heat * 0.7})`; // Cold Blue
            return `rgba(15, 15, 30, 0.15)`; // Deep Background
        };

        const updatePeople = (time) => {
            const people = peopleRef.current;

            // Randomly spawn someone if count < 4
            if (people.length < 4 && Math.random() < 0.005) {
                people.push({
                    id: Date.now(),
                    x: doorPos.x,
                    y: doorPos.y,
                    targetX: 20 + Math.random() * 60,
                    targetY: 20 + Math.random() * 60,
                    intensity: 0,
                    state: 'entering',
                    speed: 0.1 + Math.random() * 0.1,
                    waitTimer: 3000 + Math.random() * 10000
                });
            }

            people.forEach((p, index) => {
                // Movement
                const dx = p.targetX - p.x;
                const dy = p.targetY - p.y;
                const dist = Math.sqrt(dx * dx + dy * dy);

                if (dist > 1) {
                    p.x += (dx / dist) * p.speed;
                    p.y += (dy / dist) * p.speed;
                }

                // State logic
                if (p.state === 'entering') {
                    p.intensity = Math.min(0.9, p.intensity + 0.01);
                    if (dist <= 1) {
                        p.state = 'roaming';
                    }
                } else if (p.state === 'roaming') {
                    p.intensity = 0.9 + Math.sin(time * 0.002 + p.id) * 0.05;
                    p.waitTimer -= 16;

                    // Periodic small movements while roaming
                    if (Math.random() < 0.01) {
                        p.targetX = Math.max(15, Math.min(85, p.x + (Math.random() - 0.5) * 20));
                        p.targetY = Math.max(15, Math.min(80, p.y + (Math.random() - 0.5) * 20)); // Keep roaming area away from bottom door
                    }

                    if (p.waitTimer <= 0) {
                        p.state = 'exiting';
                        p.targetX = doorPos.x;
                        p.targetY = doorPos.y;
                    }
                } else if (p.state === 'exiting') {
                    if (dist <= 1) {
                        p.intensity -= 0.02;
                        if (p.intensity <= 0) {
                            people.splice(index, 1);
                        }
                    } else if (dist < 10) {
                        p.intensity = Math.max(0.4, p.intensity - 0.01);
                    }
                }
            });
        };

        const draw = (time) => {
            const { width, height } = canvas.getBoundingClientRect();
            if (canvas.width !== width || canvas.height !== height) {
                canvas.width = width;
                canvas.height = height;
            }

            updatePeople(time);

            ctx.clearRect(0, 0, width, height);

            const cellW = width / cols;
            const cellH = height / rows;

            // Draw thermal grid
            for (let i = 0; i < cols; i++) {
                for (let j = 0; j < rows; j++) {
                    const x = i * cellW;
                    const y = j * cellH;

                    let maxHeat = 0;
                    peopleRef.current.forEach(p => {
                        const dx = (i / cols) * 100 - p.x;
                        const dy = (j / rows) * 100 - p.y;
                        const distSq = dx * dx + dy * dy;
                        const heatContribution = p.intensity * Math.exp(-distSq / 60);
                        if (heatContribution > maxHeat) maxHeat = heatContribution;
                    });

                    const doorStartI = Math.floor(cols * 0.44);
                    const doorEndI = Math.ceil(cols * 0.56);
                    const doorStartJ = Math.floor(rows * 0.85);
                    const isDoor = i >= doorStartI && i <= doorEndI && j >= doorStartJ;

                    let noise = Math.sin(time * 0.001 + i * 0.2 + j * 0.3) * 0.03;
                    let heat = maxHeat + noise + 0.05;

                    if (isDoor) {
                        if (maxHeat > 0.4) {
                            ctx.fillStyle = getThermalColor(maxHeat);
                        } else {
                            ctx.fillStyle = `rgba(5, 5, 10, 0.4)`;
                        }
                    } else {
                        ctx.fillStyle = getThermalColor(heat);
                    }
                    ctx.fillRect(x + 1, y + 1, cellW - 1, cellH - 1);
                }
            }

            // Draw door frame outline snapped EXACTLY to the grid lines
            const dStartI = Math.floor(cols * 0.44);
            const dEndI = Math.ceil(cols * 0.56);
            const dStartJ = Math.floor(rows * 0.85);

            const snapStartX = dStartI * cellW;
            const snapWidth = (dEndI - dStartI + 1) * cellW;
            const snapStartY = dStartJ * cellH;
            const snapHeight = height - snapStartY;

            // 1. Outer Frame
            ctx.strokeStyle = `rgba(0, 255, 255, 0.5)`;
            ctx.lineWidth = 1.5;
            ctx.strokeRect(snapStartX, snapStartY, snapWidth, snapHeight);

            // 2. Inner Door (Small rectangle inside) - Snapped to grid
            const innerWCells = Math.floor((dEndI - dStartI) / 2);
            const innerStartI = dStartI + Math.floor(((dEndI - dStartI + 1) - innerWCells) / 2);
            const innerStartX = innerStartI * cellW;
            const innerWidth = innerWCells * cellW;
            const innerStartY = (dStartJ + 2) * cellH; // Start 2 cells down
            const innerHeight = height - innerStartY;

            ctx.setLineDash([3, 3]);
            ctx.strokeStyle = `rgba(0, 255, 255, 0.4)`;
            ctx.strokeRect(innerStartX, innerStartY, innerWidth, innerHeight);
            ctx.setLineDash([]);

            // 3. Label "ENTRY/EXIT" - Centered in door row
            ctx.fillStyle = `rgba(0, 255, 255, 0.7)`;
            ctx.font = 'bold 9px "JetBrains Mono", monospace';
            ctx.textAlign = 'center';
            ctx.fillText('ENTRY/EXIT', snapStartX + snapWidth / 2, snapStartY + cellH * 1.2);

            if (Math.floor(time / 200) !== Math.floor((time - 16) / 200)) {
                setReadings({
                    temp: (36.4 + Math.random() * 0.4).toFixed(1),
                    count: peopleRef.current.length
                });
            }

            animationFrameId = requestAnimationFrame(draw);
        };

        animationFrameId = requestAnimationFrame(draw);
        return () => cancelAnimationFrame(animationFrameId);
    }, []);

    return (
        <VisualContainer>
            <Canvas ref={canvasRef} />
            <Overlay>
                <UserCountDisplay
                    key={readings.count}
                    initial={{ scale: 1.3, opacity: 0.7 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5, type: 'spring', stiffness: 200 }}
                >
                    <UserIcon viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M20.59 22C20.59 18.13 16.74 15 12 15C7.26 15 3.41 18.13 3.41 22"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </UserIcon>
                    {readings.count}
                </UserCountDisplay>
            </Overlay>
        </VisualContainer>
    );
};

export default TechVisual;



